import { useCallback, useEffect, useState } from "react";
import {
  ANIMATION_CONFIG,
  HINT_CONFIG,
  LEVEL_CONFIG,
  getRandomAnimal,
  playButtonSound,
  playCompleteSound,
  playCorrectSound,
  playWrongSound,
} from "../../constants";
import { useGame } from "../../context";
import "./PlaceValueGame.css";

// 生成問題
const generateQuestion = (level) => {
  const config =
    LEVEL_CONFIG.PLACE_VALUE_RANGES[Math.min(level, 10)] ||
    LEVEL_CONFIG.PLACE_VALUE_RANGES[10];
  const maxNum = config.max;

  const animal = getRandomAnimal();

  // 生成目標數字
  const targetNumber = Math.floor(Math.random() * maxNum) + 1;

  // 隨機初始化算柱狀態（有時從0開始，有時有初始值）
  let initialTens = 0;
  let initialOnes = 0;

  if (level >= 3 && Math.random() > 0.5) {
    // 高等級可能有初始值
    const initialTotal = Math.floor(Math.random() * Math.min(targetNumber, 30));
    initialTens = Math.floor(initialTotal / 10);
    initialOnes = initialTotal % 10;
  }

  return {
    targetNumber,
    initialTens,
    initialOnes,
    animal,
    question: `幫${animal.name}用算柱表示 ${targetNumber}！`,
  };
};

// 算柱珠子組件
const CountingRod = ({
  value,
  type,
  onAdd,
  onRemove,
  animal,
  isCarrying,
  canCarry,
}) => {
  const maxValue = 9;
  const itemEmoji = animal.itemEmoji;
  const label =
    type === "tens" ? `十位(📦=${itemEmoji}×10)` : `個位(${itemEmoji})`;

  // 個位可以進位時不禁用，十位達到9時禁用
  // 當value=10時（進位中）也禁用
  const isAddDisabled =
    type === "tens"
      ? value >= maxValue
      : (value >= maxValue && !canCarry) || value >= 10;

  // 顯示值（用於顯示10）
  // const displayValue = value;
  const isTenState = type === "ones" && value === 10;

  return (
    <div
      className={`counting-rod ${type} ${isCarrying ? "carrying" : ""} ${
        isTenState ? "ten-state" : ""
      }`}
    >
      {/* 數字顯示 */}
      <div className="rod-value">{value}</div>

      {/* 算珠顯示區 - 由下往上堆疊 */}
      <div className="rod-display">
        {Array(value)
          .fill(null)
          .map((_, index) => (
            <div
              key={index}
              className={`rod-item ${type} ${
                isCarrying && type === "tens" && index === value - 1
                  ? "new-carry"
                  : ""
              }`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {type === "tens" ? (
                <div className="tens-box">
                  <span className="box-icon">📦</span>
                  <span className="box-count">10{itemEmoji}</span>
                </div>
              ) : (
                <span className="ones-dot">{itemEmoji}</span>
              )}
            </div>
          ))}

        {/* 空位顯示 */}
        {value === 0 && (
          <div className="empty-slot">{type === "tens" ? "(空)" : "(空)"}</div>
        )}
      </div>

      {/* 標籤 */}
      <div className="rod-label">{label}</div>

      {/* 控制按鈕在標籤下方 */}
      <div className="rod-controls">
        <button
          className="control-btn add"
          onClick={onAdd}
          disabled={isAddDisabled}
        >
          +
        </button>
        <button
          className="control-btn remove"
          onClick={onRemove}
          disabled={value <= 0}
        >
          −
        </button>
      </div>
    </div>
  );
};

// 中國數字讀作顯示組件
const ChineseNumberDisplay = ({ tens, ones, onReset }) => {
  // 轉換數字為中文
  const chineseDigits = [
    "零",
    "一",
    "二",
    "三",
    "四",
    "五",
    "六",
    "七",
    "八",
    "九",
    "十",
  ];

  const getChineseNumber = (t, o) => {
    const total = t * 10 + o;
    if (total === 0) return "零";
    if (total === 10) return "十";
    if (total < 10) return chineseDigits[total];
    if (total < 20) return `十${o === 0 ? "" : chineseDigits[o]}`;
    if (o === 0) return `${chineseDigits[t]}十`;
    return `${chineseDigits[t]}十${chineseDigits[o]}`;
  };

  return (
    <div className="chinese-number-display">
      <div className="chinese-label">讀作：</div>
      <div className="chinese-number">{getChineseNumber(tens, ones)}</div>
      <button className="reset-btn" onClick={onReset}>
        🔄 重置
      </button>
    </div>
  );
};

// 數字顯示組件
const NumberDisplay = ({ tens, ones, animal }) => {
  const total = tens * 10 + ones;
  const itemEmoji = animal?.itemEmoji || "🔵";

  return (
    <div className="number-display">
      <div className="number-breakdown">
        <span className="breakdown-item tens">{tens} × 📦</span>
        <span className="breakdown-plus">+</span>
        <span className="breakdown-item ones">
          {ones} × {itemEmoji}
        </span>
        <span className="breakdown-equals">=</span>
        <span className="total-value">{total}</span>
      </div>
    </div>
  );
};

const PlaceValueGame = ({ onBack }) => {
  const { state, actions } = useGame();
  const [question, setQuestion] = useState(null);
  const [tens, setTens] = useState(0);
  const [ones, setOnes] = useState(0);
  const [showResult, setShowResult] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [isCarrying, setIsCarrying] = useState(false);
  const [showCarryToast, setShowCarryToast] = useState(false);

  const currentLevel = state.gameProgress.placeValue.level;
  const gameProgress = state.gameProgress.placeValue;
  const currentTotal = tens * 10 + ones;

  // 初始化遊戲
  useEffect(() => {
    actions.startGame("placeValue");
    generateNewQuestion();

    return () => {
      actions.endGame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 等級提升音效
  useEffect(() => {
    if (state.showLevelUp) {
      playCompleteSound();
    }
  }, [state.showLevelUp]);

  // 生成新問題
  const generateNewQuestion = useCallback(() => {
    const newQuestion = generateQuestion(currentLevel);
    setQuestion(newQuestion);
    actions.setQuestion(newQuestion);
    setTens(newQuestion.initialTens);
    setOnes(newQuestion.initialOnes);
    setShowResult(null);
    setShowHint(false);
    setHintsUsed(0);
  }, [currentLevel, actions]);

  // 添加個位
  const handleAddOne = () => {
    if (ones < 9) {
      setOnes(ones + 1);
    } else {
      // 進位動畫：先顯示10，再進位
      if (tens < 9) {
        // 先顯示10個
        setOnes(10);

        // 延遲後開始進位動畫
        setTimeout(() => {
          setIsCarrying(true);
          setShowCarryToast(true);
          setOnes(0);

          setTimeout(() => {
            setTens(tens + 1);
            setTimeout(() => {
              setIsCarrying(false);
              setShowCarryToast(false);
            }, 800);
          }, 300);
        }, 600); // 顯示10的時間
      }
    }
  };

  // 減少個位
  const handleRemoveOne = () => {
    if (ones > 0) {
      setOnes(ones - 1);
    } else if (tens > 0) {
      // 退位
      setOnes(9);
      setTens(tens - 1);
    }
  };

  // 添加十位
  const handleAddTen = () => {
    if (tens < 9) {
      setTens(tens + 1);
    }
  };

  // 減少十位
  const handleRemoveTen = () => {
    if (tens > 0) {
      setTens(tens - 1);
    }
  };

  // 重置
  const handleReset = () => {
    playButtonSound();
    setTens(question.initialTens);
    setOnes(question.initialOnes);
  };

  // 檢查答案
  const handleCheck = () => {
    playButtonSound();
    if (currentTotal === question.targetNumber) {
      playCorrectSound();
      setShowResult("correct");
      actions.answerCorrect("placeValue", hintsUsed > 0);

      setTimeout(() => {
        generateNewQuestion();
      }, ANIMATION_CONFIG.SUCCESS_DELAY);
    } else if (currentTotal < question.targetNumber) {
      playWrongSound();
      setShowResult("needMore");
      actions.showFeedback(
        `還不夠哦！還差 ${question.targetNumber - currentTotal}`,
        "warning",
      );
      setTimeout(() => setShowResult(null), 1500);
    } else {
      playWrongSound();
      setShowResult("tooMany");
      actions.showFeedback(
        `太多了！多了 ${currentTotal - question.targetNumber}`,
        "warning",
      );
      setTimeout(() => setShowResult(null), 1500);
    }
  };

  // 提示
  const handleHint = () => {
    playButtonSound();
    if (hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION) {
      actions.showFeedback("這題已經沒有更多提示了！", "info");
      return;
    }

    setShowHint(true);
    setHintsUsed((prev) => prev + 1);
    actions.useHint();
  };

  if (!question) {
    return (
      <div className="game-loading">
        <span className="loading-emoji">🔄</span>
        <span>載入中...</span>
      </div>
    );
  }

  const targetTens = Math.floor(question.targetNumber / 10);
  const targetOnes = question.targetNumber % 10;

  return (
    <div
      className="place-value-game"
      style={{ backgroundColor: question.animal.bgColor }}
    >
      {/* 遊戲頭部 */}
      <div className="game-header">
        <button className="back-button" onClick={onBack}>
          <span>←</span> 返回
        </button>

        <div className="game-info">
          <div className="info-item">
            <span className="info-icon">🎯</span>
            <span className="info-value">等級 {currentLevel}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">⭐</span>
            <span className="info-value">{state.totalStars}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">🏆</span>
            <span className="info-value">{state.totalScore}</span>
          </div>
        </div>
      </div>

      {/* 進度條 */}
      <div className="level-progress">
        <div className="progress-label">
          升級進度：{gameProgress.correctCount % 5} / 5
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(gameProgress.correctCount % 5) * 20}%` }}
          />
        </div>
      </div>

      {/* 主遊戲區域 */}
      <div className="game-main">
        {/* 目標問題與算柱區域 - 三欄并排 */}
        <div className="main-display-row">
          {/* 目標 */}
          <div className="target-section">
            <span className="animal-emoji">{question.animal.emoji}</span>
            <div className="target-box">
              <span className="target-label">目標</span>
              <span className="target-number">{question.targetNumber}</span>
            </div>
          </div>

          {/* 十位 */}
          <CountingRod
            value={tens}
            type="tens"
            onAdd={handleAddTen}
            onRemove={handleRemoveTen}
            animal={question.animal}
            isCarrying={isCarrying}
            canCarry={false}
          />

          {/* 個位 */}
          <CountingRod
            value={ones}
            type="ones"
            onAdd={handleAddOne}
            onRemove={handleRemoveOne}
            animal={question.animal}
            isCarrying={false}
            canCarry={tens < 9}
          />
        </div>

        {/* 數字顯示 */}
        <NumberDisplay tens={tens} ones={ones} animal={question.animal} />

        {/* 中國數字讀作 */}
        <ChineseNumberDisplay tens={tens} ones={ones} onReset={handleReset} />

        {/* 操作按鈕 */}
        <div className="action-section">
          <button
            className={`check-button ${showResult}`}
            onClick={handleCheck}
          >
            ✓ 檢查答案
          </button>

          <div className="secondary-actions">
            <button
              className="hint-button"
              onClick={handleHint}
              disabled={hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION}
            >
              💡 提示 ({HINT_CONFIG.MAX_HINTS_PER_QUESTION - hintsUsed}次)
            </button>
          </div>
        </div>

        {/* 結果反饋 */}
        {showResult === "correct" && (
          <div className="result-overlay correct">
            <div className="result-content">
              <span className="result-emoji">🎉</span>
              <span className="result-text">太棒了！</span>
              <span className="result-detail">
                {tens} 條十 + {ones} 個一 = {currentTotal}
              </span>
            </div>
          </div>
        )}

        {showResult === "needMore" && (
          <div className="result-message need-more">
            <span>📈 還不夠，需要更多！</span>
          </div>
        )}

        {showResult === "tooMany" && (
          <div className="result-message too-many">
            <span>📉 太多了，減少一些！</span>
          </div>
        )}
      </div>

      {/* 提示彈窗 */}
      {showHint && (
        <div className="hint-overlay" onClick={() => setShowHint(false)}>
          <div className="hint-card" onClick={(e) => e.stopPropagation()}>
            <div className="hint-header">
              <span className="hint-icon">💡</span>
              <span className="hint-title">提示</span>
            </div>
            <div className="hint-content">
              <div className="hint-breakdown">
                <div className="hint-item">
                  <span className="hint-label">目標數字：</span>
                  <span className="hint-value">{question.targetNumber}</span>
                </div>
                <div className="hint-item">
                  <span className="hint-label">需要幾條十：</span>
                  <span className="hint-value">{targetTens} 條</span>
                </div>
                <div className="hint-item">
                  <span className="hint-label">需要幾個一：</span>
                  <span className="hint-value">{targetOnes} 個</span>
                </div>
                <div className="hint-equation">
                  {targetTens} × 10 + {targetOnes} × 1 = {question.targetNumber}
                </div>
              </div>
            </div>
            <button
              className="hint-close-btn"
              onClick={() => setShowHint(false)}
            >
              我知道了 ✨
            </button>
          </div>
        </div>
      )}

      {/* 升級動畫 */}
      {state.showLevelUp && (
        <div className="level-up-overlay">
          <div className="level-up-content">
            <span className="level-up-emoji">🎊</span>
            <span className="level-up-text">等級提升！</span>
            <span className="level-up-level">Level {currentLevel}</span>
            <button onClick={actions.hideLevelUp} className="level-up-btn">
              太棒了！
            </button>
          </div>
        </div>
      )}

      {/* 進位提示 Toast - 螢幕正中央 */}
      {showCarryToast && (
        <div className="carry-toast-overlay">
          <div className="carry-toast">
            <span className="carry-toast-icon">📦</span>
            <span className="carry-toast-text">滿10進1！</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceValueGame;
